import {
  Close,
  CloudDownload,
  CloudUpload,
  DescriptionOutlined,
  PictureAsPdfOutlined,
  Visibility,
  WarningAmber,
  Schedule,
  CurrencyRupee,
  CheckCircleOutline,
  ReceiptLongOutlined,
} from "@mui/icons-material";
import { Dialog, DialogContent, DialogTitle, IconButton } from "@mui/material";
import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import { isCashPayment } from "./constants";
import {
  classifyDocUrl,
  isApproveDisabled,
  isDeclineDisabled,
  isFilled,
  buildIpdUpdatePayload,
  buildGenericFieldPayload,
  formatToDateTimeLocal,
} from "./pureLogic";
import * as api from "./billCollection.service";
import CollectionUpdation from "./CollectionUpdation";
import CollectionHistory from "./CollectionHistory";

const isViewerOnly = () => localStorage.getItem("viewer") === "true";

const getUser = () => {
  const userObj = JSON.parse(localStorage.getItem("user") || "{}") || {};
  return {
    userId: userObj?.userId || localStorage.getItem("userId") || "",
    userName:
      userObj?.username || userObj?.name || localStorage.getItem("userName") || "",
  };
};

const buildForm = (data) => ({
  finalDateOfDischarge: formatToDateTimeLocal(
    data?.discharge_date ||
      data?.dischargeDate ||
      (typeof data?.finalDateOfDischarge === "object"
        ? data?.finalDateOfDischarge?.value
        : data?.finalDateOfDischarge)
  ),
  admittedToRoom: formatToDateTimeLocal(data?.actual_admission_date),
  actualOtStartTime: formatToDateTimeLocal(data?.actualOtStartTime),
  actualOtEndTime: formatToDateTimeLocal(data?.actualOtEndTime),
  totalCollectibleAtAdmission: data?.totalCollectibleAtAdmission || 0,
  totalCollectibleAtDischarge: data?.totalCollectibleAtDischarge || 0,
  totalCollectedOps: data?.totalCollectedAtAdmission || 0,
  totalCollectedDisOps: data?.totalCollectedAtDischarge || 0,
});

/**
 * Document / Collection Actions tab — mirrors the Actions pane of central_dashboard's
 * `BillCollectionModalComponent` (File Section | Patient info + Verification &
 * Action / Collection Updation / History).
 *
 * Approve / Decline are STAGED in the parent (`onStageDecision`), not saved here.
 * They are committed together when "Send for Approval" succeeds its first step.
 */
const DocumentActionsTab = ({
  data,
  urlArray,
  isDisable,
  isDetailedBillRequired,
  isFinalBill,
  finalBillAmount,
  setFinalBillAmount,
  netBillAmount,
  setNetBillAmount,
  pendingDecisionMap = {},
  onStageDecision,
  commitPendingChanges,
  // Lens gate — forwarded to CollectionUpdation → Collectible sub-tab.
  isLensApplicable = false,
  hasLensDecision = false,
  lensChoice = "",
  lensSaved = false,
  lensLocked = false,
  onLensChoice,
  onReopenLensModal,
  onRefresh,
  onUpdateIpdNo,
  onUpdateInfo,
  onGoToUpload,
  onStatusChange,
  onApprovalSuccess,
}) => {
  const [activeFileTab, setActiveFileTab] = useState("pdf");
  const [activeRightTab, setActiveRightTab] = useState("verification");
  const [currentDocIndex, setCurrentDocIndex] = useState(0);
  const [pdfLoading, setPdfLoading] = useState(false);
  // Signed viewer URLs keyed by raw url, so list changes never mis-map them.
  const [signedMap, setSignedMap] = useState({});
  const [ipdNoOnBill, setIpdNoOnBill] = useState("");
  const [form, setForm] = useState(() => buildForm(data));

  const [declineModalOpen, setDeclineModalOpen] = useState(false);
  const [declineIndex, setDeclineIndex] = useState(null);
  const [declineReason, setDeclineReason] = useState("");

  const verificationTopRef = useRef(null);

  const paymentMode = data?.payment_mode || "";
  const isCash = isCashPayment(paymentMode);
  const appId = data?.appID || "";
  const leadId = data?.leadId || data?.lead_id || "";

  // Re-derive prefilled fields whenever `data` is rebuilt (central
  // `fetchAdmissionCollectible`).
  useEffect(() => {
    if (!data) return;
    setIpdNoOnBill(
      typeof data?.ipdNoOnBill === "object"
        ? data?.ipdNoOnBill?.value || ""
        : data?.ipdNoOnBill || ""
    );
    setForm(buildForm(data));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    data?.appID,
    data?.discharge_date,
    data?.actual_admission_date,
    data?.actualOtStartTime,
    data?.actualOtEndTime,
    data?.totalCollectibleAtAdmission,
    data?.totalCollectibleAtDischarge,
    data?.totalCollectedAtAdmission,
    data?.totalCollectedAtDischarge,
    data?.ipdNoOnBill,
  ]);

  const loadDoc = useCallback(
    async (index) => {
      const doc = (urlArray || [])[index];
      if (!doc?.url) return;
      setCurrentDocIndex(index);
      if (signedMap[doc.url]) return; // already signed (idempotent)
      setPdfLoading(true);
      try {
        const res = await api.getSignedUrl([doc.url]);
        const signed = res?.result?.[0] || res?.[0] || doc.url;
        const { isImage, viewerUrl } = classifyDocUrl(doc.url, signed);
        setSignedMap((prev) => ({ ...prev, [doc.url]: { urlSign: viewerUrl, isImage } }));
      } catch {
        toast.error("Unable to load document");
      } finally {
        setPdfLoading(false);
      }
    },
    [urlArray, signedMap]
  );

  // Load the first document when the list becomes available / changes.
  const firstUrl = urlArray?.[0]?.url;
  useEffect(() => {
    if (urlArray?.length) loadDoc(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [firstUrl, urlArray?.length]);

  const downloadAttachment = async (item) => {
    try {
      const res = await api.getSignedUrl([item.url]);
      const signedUrl = res?.result?.[0] || res?.[0] || item.url;
      const link = document.createElement("a");
      link.href = signedUrl;
      link.download = item.name || "document";
      link.target = "_blank";
      link.click();
    } catch {
      toast.error("Download failed");
    }
  };

  const onFieldChange = async (fieldName, value) => {
    if (value === null || value === undefined || value === "") return;
    try {
      const { userId, userName } = getUser();
      const payload = buildGenericFieldPayload(
        appId,
        leadId,
        fieldName,
        value,
        userId,
        userName
      );
      const res = await api.updateCentralDashReporting(payload);
      if (res?.status === 200 || res?.status === undefined) {
        toast.success(
          fieldName.charAt(0).toUpperCase() + fieldName.slice(1) + " updated Successfully"
        );
        onUpdateInfo?.(fieldName);
      }
    } catch {
      toast.error("Something went wrong with update");
    }
  };

  const updateIPD = async (value) => {
    try {
      const { userId } = getUser();
      await api.updateCentralDashReporting(buildIpdUpdatePayload(appId, value, userId));
      toast.success("Updated Successfully");
      onRefresh?.();
      onUpdateIpdNo?.(value);
    } catch {
      toast.error("Something Went Wrong");
    }
  };

  // Net Bill Amount is mandatory for Cash. Blocks Approve / Decline until entered.
  const isNetBillMissing = () => {
    if (!isCash) return false;
    if (!isFilled(netBillAmount)) {
      toast.error("Net Bill Amount is required");
      return true;
    }
    return false;
  };

  // NOTHING is saved here. Decisions are kept in memory (parent) and sent
  // together when the user clicks "Send for Approval".
  const changeStatus = (status, index) => {
    if (isViewerOnly()) {
      toast.warning("You are only allowed to view.");
      return;
    }
    const doc = (urlArray || [])[index];
    // Documents added on the Upload tab are not saved yet -> cannot be decided.
    if (!doc || doc.isStaged) return;
    if (isNetBillMissing()) return;
    const id = doc._id;

    if (status === "Approved") {
      if (isCash && finalBillAmount === null) {
        toast.error("Final Bill Amount is required");
        return;
      }
      if (window.confirm("Are you sure you want to Approve this ?")) {
        onStageDecision(index, id, { _id: id, status: "Approved" }, "Approved");
        toast.success("Approved. It will be saved on Send for Approval");
      }
    } else if (status === "Declined") {
      setDeclineIndex(index);
      setDeclineReason("");
      setDeclineModalOpen(true);
    }
  };

  const confirmDecline = () => {
    if (!declineReason.trim()) {
      toast.error("Please Enter Decline Reason");
      return;
    }
    const doc = (urlArray || [])[declineIndex];
    onStageDecision(
      declineIndex,
      doc?._id,
      { _id: doc?._id, status: "Declined", declineReason },
      "Declined"
    );
    toast.success("Declined. It will be saved on Send for Approval");
    setDeclineModalOpen(false);
  };

  const goToCollectionUpdation = () => {
    setActiveRightTab("collection");
    setTimeout(() => {
      verificationTopRef.current?.scrollIntoView?.({ behavior: "smooth", block: "start" });
    }, 0);
  };

  const currentDoc = (urlArray || [])[currentDocIndex] || {};
  const currentSigned = signedMap[currentDoc.url] || {};
  const isDecisionPending = (item) =>
    !!pendingDecisionMap[item?._id || item?.url];

  if (!urlArray?.length) {
    return (
      <div className="bcm-actions-pane">
        <div className="bcm-actions-empty">
          <DescriptionOutlined className="bcm-actions-empty-icon" />
          <span className="bcm-actions-empty-title">No documents available</span>
          <span className="bcm-actions-empty-sub">
            Upload documents first from the &quot;Document Upload&quot; tab to start
            reviewing and taking actions.
          </span>
          <button
            type="button"
            className="bcm-btn-outline"
            style={{ marginTop: 12 }}
            onClick={() => onGoToUpload?.()}
          >
            <CloudUpload sx={{ fontSize: 15 }} /> Go to Document Upload
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="bcm-actions-pane">
      <div className="bad-grid">
        {/* LEFT: FILE SECTION */}
        <div className="bad-file-section">
          <div className="bad-card">
            <div className="bad-card-title">File Section</div>
            <div className="bad-file-tabs">
              <button
                type="button"
                className={`bad-file-tab ${activeFileTab === "pdf" ? "active" : ""}`}
                onClick={() => setActiveFileTab("pdf")}
              >
                View PDF
              </button>
              <button
                type="button"
                className={`bad-file-tab ${activeFileTab === "attachments" ? "active" : ""}`}
                onClick={() => setActiveFileTab("attachments")}
              >
                Attachments ({urlArray.length})
              </button>
            </div>

            {activeFileTab === "pdf" && (
              <div className="bad-pdf-viewer">
                {pdfLoading ? (
                  <div className="bad-pdf-loader">
                    <span className="bad-spinner" />
                    <span className="bad-pdf-loader-text">Loading document...</span>
                  </div>
                ) : currentSigned.urlSign ? (
                  <div className="bad-pdf-frame">
                    {currentSigned.isImage ? (
                      <img
                        src={currentSigned.urlSign}
                        alt="Document"
                        className="bad-pdf-img"
                      />
                    ) : (
                      <iframe src={currentSigned.urlSign} title="Document viewer" />
                    )}
                  </div>
                ) : (
                  <div className="bad-empty">No document uploaded.</div>
                )}
              </div>
            )}

            {activeFileTab === "attachments" && (
              <div className="bad-attachments">
                {urlArray.map((item, i) => (
                  <div className="bad-attach-row" key={(item.url || "") + i}>
                    <div className="bad-attach-info">
                      <span className="bad-attach-name">
                        <PictureAsPdfOutlined className="bad-doc-ic" sx={{ fontSize: 16 }} />
                        {item.name ? item.name : "Document " + (i + 1)}
                      </span>
                      {(item.clickedBy || item.timestamp) && (
                        <span className="bad-attach-sub">
                          {item.clickedBy}
                          {item.clickedBy && item.timestamp ? " | " : ""}
                          {item.timestamp
                            ? new Date(item.timestamp).toLocaleString("en-US", {
                                dateStyle: "medium",
                                timeStyle: "short",
                              })
                            : ""}
                        </span>
                      )}
                    </div>
                    <div className="bad-attach-right">
                      {item.status && (
                        <span className="bad-pill bad-pill-pending">{item.status}</span>
                      )}
                      <button
                        type="button"
                        className="bad-attach-icon"
                        title="View"
                        onClick={() => {
                          setActiveFileTab("pdf");
                          loadDoc(i);
                        }}
                      >
                        <Visibility sx={{ fontSize: 16 }} />
                      </button>
                      <button
                        type="button"
                        className="bad-attach-icon"
                        title="Download"
                        onClick={() => downloadAttachment(item)}
                      >
                        <CloudDownload sx={{ fontSize: 16 }} />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* RIGHT: INFO + TABS */}
        <div className="bad-right-section">
          {/* Patient info */}
          <div className="bad-card bad-info-card">
            <div className="bad-info-head">
              <span className="bad-info-title">PATIENT &amp; VISIT INFORMATION</span>
            </div>
            <div className="bad-info-grid">
              <div className="bad-info-item">
                <span className="bad-info-label">Patient Name</span>
                <span className="bad-info-value">{data?.name || data?.patient_name || "-"}</span>
              </div>
              <div className="bad-info-item">
                <span className="bad-info-label">Patient City</span>
                <span className="bad-info-value">{data?.city || "-"}</span>
              </div>
              <div className="bad-info-item">
                <span className="bad-info-label">Hospital Name</span>
                <span className="bad-info-value">
                  {data?.hospital || data?.hospital_name || "-"}
                </span>
              </div>
              <div className="bad-info-item">
                <span className="bad-info-label">Payment Mode</span>
                <span className="bad-info-value bad-info-accent">{paymentMode || "-"}</span>
              </div>
            </div>
          </div>

          {/* Sub-tab strip */}
          <div className="bad-tab-strip" ref={verificationTopRef}>
            <button
              type="button"
              className={`bad-tab ${activeRightTab === "verification" ? "active" : ""}`}
              onClick={() => setActiveRightTab("verification")}
            >
              VERIFICATION &amp; ACTION
            </button>
            {isFinalBill && (
              <>
                <button
                  type="button"
                  className={`bad-tab ${activeRightTab === "collection" ? "active" : ""}`}
                  onClick={() => setActiveRightTab("collection")}
                >
                  COLLECTION UPDATION
                </button>
                <button
                  type="button"
                  className={`bad-tab ${activeRightTab === "history" ? "active" : ""}`}
                  onClick={() => setActiveRightTab("history")}
                >
                  HISTORY
                </button>
              </>
            )}
          </div>

          {/* VERIFICATION & ACTION */}
          {activeRightTab === "verification" && (
            <div className="bad-card bad-tab-body">
              <div className="bad-tab-label-row">
                <span className="bad-tab-label">VERIFICATION &amp; ACTION</span>
                {/* Shown only when getFinalBillCentral flags isDetailedBillRequired. */}
                {isDetailedBillRequired && (
                  <span className="bad-detail-bill-badge">
                    <WarningAmber sx={{ fontSize: 14 }} /> Detail Bill Required
                  </span>
                )}
              </div>

              {isFinalBill && (
                <div className="bad-section">
                  <div className="bad-section-head">
                    <span className="bad-section-icon bad-ic-bill">
                      <ReceiptLongOutlined sx={{ fontSize: 16 }} />
                    </span>
                    <span className="bad-section-title">Bill Details</span>
                  </div>
                  <div className="bad-fields-grid">
                    <div className="bad-field">
                      <label>IPD no. on Bill</label>
                      <input
                        type="text"
                        className="bad-input"
                        value={ipdNoOnBill}
                        onChange={(e) => setIpdNoOnBill(e.target.value)}
                        onBlur={(e) => {
                          const original =
                            typeof data?.ipdNoOnBill === "object"
                              ? data?.ipdNoOnBill?.value || ""
                              : data?.ipdNoOnBill || "";
                          if (e.target.value !== original) updateIPD(e.target.value);
                        }}
                      />
                    </div>
                    {isCash && (
                      <>
                        <div className="bad-field">
                          <label>
                            Final Bill Amount <span className="req">*</span>
                          </label>
                          <input
                            type="number"
                            className="bad-input"
                            value={finalBillAmount ?? ""}
                            onChange={(e) =>
                              setFinalBillAmount(e.target.value === "" ? null : e.target.value)
                            }
                          />
                        </div>
                        <div className="bad-field">
                          <label>Net Bill Amount</label>
                          <input
                            type="number"
                            className="bad-input"
                            value={netBillAmount ?? ""}
                            onChange={(e) =>
                              setNetBillAmount(e.target.value === "" ? null : e.target.value)
                            }
                          />
                        </div>
                      </>
                    )}
                  </div>
                </div>
              )}

              <div className="bad-two-sections">
                <div className="bad-section">
                  <div className="bad-section-head">
                    <span className="bad-section-icon bad-ic-time">
                      <Schedule sx={{ fontSize: 16 }} />
                    </span>
                    <span className="bad-section-title">Admission, Discharge &amp; OT Timings</span>
                  </div>
                  <div className="bad-fields-grid">
                    {[
                      { key: "finalDateOfDischarge", label: "Actual Date of Discharge" },
                      { key: "admittedToRoom", label: "Actual Room Admission Time" },
                      { key: "actualOtStartTime", label: "Actual OT Start Time" },
                      { key: "actualOtEndTime", label: "Actual OT End Time" },
                    ].map(({ key, label }) => (
                      <div className="bad-field" key={key}>
                        <label>{label}</label>
                        <input
                          type="datetime-local"
                          className="bad-input"
                          value={form[key] || ""}
                          onChange={(e) => {
                            setForm((prev) => ({ ...prev, [key]: e.target.value }));
                            onFieldChange(key, e.target.value);
                          }}
                        />
                      </div>
                    ))}
                  </div>
                </div>

                <div className="bad-section">
                  <div className="bad-section-head">
                    <span className="bad-section-icon bad-ic-money">
                      <CurrencyRupee sx={{ fontSize: 16 }} />
                    </span>
                    <span className="bad-section-title">Collection Summary</span>
                  </div>
                  <div className="bad-fields-grid">
                    <div className="bad-field">
                      <label>Total Collectible at Discharge</label>
                      <input
                        type="tel"
                        className="bad-input readonly"
                        value={form.totalCollectibleAtDischarge}
                        readOnly
                      />
                    </div>
                    <div className="bad-field">
                      <label>Total Collectible at Admission</label>
                      <input
                        type="tel"
                        className="bad-input readonly"
                        value={form.totalCollectibleAtAdmission}
                        readOnly
                      />
                    </div>
                    <div className="bad-field">
                      <label>Total Collected at Admission</label>
                      <input
                        type="tel"
                        className="bad-input"
                        value={form.totalCollectedOps}
                        onChange={(e) =>
                          setForm((prev) => ({ ...prev, totalCollectedOps: e.target.value }))
                        }
                        onBlur={(e) => onFieldChange("totalCollectedOps", e.target.value)}
                      />
                    </div>
                    <div className="bad-field">
                      <label>Total Collected at Discharge</label>
                      <input
                        type="tel"
                        className="bad-input"
                        value={form.totalCollectedDisOps}
                        onChange={(e) =>
                          setForm((prev) => ({ ...prev, totalCollectedDisOps: e.target.value }))
                        }
                        onBlur={(e) => onFieldChange("totalCollectedDisOps", e.target.value)}
                      />
                    </div>
                  </div>
                </div>
              </div>

              <div className="bad-section">
                <div className="bad-section-head">
                  <span className="bad-section-icon bad-ic-decision">
                    <CheckCircleOutline sx={{ fontSize: 16 }} />
                  </span>
                  <span className="bad-section-title">Document Decision</span>
                </div>
                {urlArray.map((item, i) => (
                  <div className="bad-decision-row" key={(item.url || "") + i}>
                    <span className="bad-decision-name">
                      <PictureAsPdfOutlined className="bad-doc-ic" sx={{ fontSize: 15 }} />
                      Document {i + 1}
                      {item.status && (
                        <span className="bad-decision-status">
                          &mdash; {item.status}
                          {isDecisionPending(item) && " (not saved yet)"}
                          {!isDecisionPending(item) &&
                            item.source === "Central Dashboard" &&
                            " (By Central Dashboard)"}
                        </span>
                      )}
                      {item.isStaged && !item.committed && (
                        <span className="bad-decision-status">
                          &mdash; New upload (can be reviewed after Send for Approval)
                        </span>
                      )}
                    </span>
                    <span className="bad-decision-actions">
                      <button
                        type="button"
                        className="bad-btn bad-btn-success"
                        disabled={isApproveDisabled(item, isDisable)}
                        onClick={() => changeStatus("Approved", i)}
                      >
                        Approve
                      </button>
                      <button
                        type="button"
                        className="bad-btn bad-btn-danger"
                        disabled={isDeclineDisabled(item, isDisable)}
                        onClick={() => changeStatus("Declined", i)}
                      >
                        Decline
                      </button>
                    </span>
                  </div>
                ))}
                {isFinalBill && (
                  <div className="bad-goto-row">
                    <button
                      type="button"
                      className="bcm-btn-primary"
                      onClick={goToCollectionUpdation}
                    >
                      Go to Collection Updation
                    </button>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* COLLECTION UPDATION */}
          {activeRightTab === "collection" && isFinalBill && (
            <div className="bad-card bad-tab-body">
              <CollectionUpdation
                appointmentId={appId}
                leadId={leadId}
                collectionStatus={
                  typeof data?.collectionStatus === "object"
                    ? data.collectionStatus?.status
                    : data?.collectionStatus
                }
                isInsFBStatus={data?.isInsFBStatus}
                paymentMode={paymentMode}
                netBillAmount={netBillAmount}
                isLensApplicable={isLensApplicable}
                hasLensDecision={hasLensDecision}
                lensChoice={lensChoice}
                lensSaved={lensSaved}
                lensLocked={lensLocked}
                onLensChoice={onLensChoice}
                onReopenLensModal={onReopenLensModal}
                beforeSend={commitPendingChanges}
                onStatusChange={onStatusChange}
                onApprovalSuccess={onApprovalSuccess}
              />
            </div>
          )}

          {/* HISTORY */}
          {activeRightTab === "history" && isFinalBill && (
            <div className="bad-card bad-tab-body">
              <CollectionHistory appointmentId={appId} />
            </div>
          )}
        </div>
      </div>

      {/* Decline Reason Dialog (central DeclineReasonComponent parity) */}
      <Dialog
        open={declineModalOpen}
        onClose={() => setDeclineModalOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle
          sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}
        >
          Decline Reason
          <IconButton onClick={() => setDeclineModalOpen(false)} size="small">
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <textarea
            className="bad-input"
            rows={3}
            value={declineReason}
            onChange={(e) => setDeclineReason(e.target.value)}
            style={{ resize: "vertical", minHeight: 80, width: "100%", height: "auto" }}
          />
          <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 12 }}>
            <button
              type="button"
              className="bad-btn bad-btn-danger"
              onClick={() => setDeclineModalOpen(false)}
            >
              Cancel
            </button>
            <button type="button" className="bcm-btn-primary" onClick={confirmDecline}>
              Submit
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default DocumentActionsTab;
